import { useState, useEffect } from 'react';
import { message, Modal, Input, InputNumber, Typography, Tabs, Alert, Table, Tag, Button, Space, Popconfirm, Pagination } from 'antd';
import { CopyOutlined, PlusOutlined, DeleteOutlined, ApiOutlined, CodeOutlined, EditOutlined, SwapOutlined, EyeOutlined, EyeInvisibleOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { apiKeyAPI, userAPI, modelAPI } from '../services/api';
import { t } from '../utils/i18n';
import { copyToClipboard } from '../utils/clipboard';

const { Text } = Typography;

const APIKeys = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  
  // 新增：多 Key 管理状态
  const [apiKeys, setApiKeys] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newCreditLimit, setNewCreditLimit] = useState(0); // 额度限制，0表示不限制
  const [createLoading, setCreateLoading] = useState(false);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null); // 新创建的 Key（显示一次）
  
  // 编辑状态
  const [editingKeyId, setEditingKeyId] = useState(null);
  const [editingKeyName, setEditingKeyName] = useState('');

  // 当前选中的代码示例 tab
  const [activeCodeTab, setActiveCodeTab] = useState('curl');

  // API Keys 分页状态
  const [keyPage, setKeyPage] = useState(1);
  const [keyPageSize, setKeyPageSize] = useState(10);

  // 模型分页状态
  const [modelPage, setModelPage] = useState(1);
  const [modelPageSize, setModelPageSize] = useState(10);

  useEffect(() => {
    fetchUserInfo();
    fetchModels();
    fetchApiKeys();
  }, []);

  const fetchUserInfo = async () => {
    try {
      const response = await userAPI.getInfo();
      if (response.code === 0) {
        setUserInfo(response.data);
      }
    } catch (error) {
      console.error('Fetch user info failed:', error);
    }
  };

  const fetchModels = async () => {
    try {
      const response = await modelAPI.getAvailable();
      if (response.code === 0) {
        setModels(response.data.list || []);
      }
    } catch (error) {
      console.error('Fetch models failed:', error);
    }
  };

  // 获取用户的 Key 列表
  const fetchApiKeys = async () => {
    try {
      const response = await apiKeyAPI.list();
      if (response.code === 0) {
        setApiKeys(response.data.list || []);
      }
    } catch (error) {
      console.error('Fetch API keys failed:', error);
    }
  };

  // 创建新的 Key
  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      message.error(t('please_enter_key_name'));
      return;
    }
    
    setCreateLoading(true);
    try {
      const response = await apiKeyAPI.create(newKeyName.trim(), newCreditLimit);
      if (response.code === 0) {
        message.success(t('created_success_copy'));
        setNewlyCreatedKey(response.data);
        setNewKeyName('');
        setNewCreditLimit(0);
        setCreateModalVisible(false);
        fetchApiKeys();
      } else {
        message.error(response.message || t('created_failed'));
      }
    } catch (error) {
      message.error(t('created_failed_retry'));
    } finally {
      setCreateLoading(false);
    }
  };

  // 删除 Key
  const handleDeleteKey = async (id) => {
    try {
      const response = await apiKeyAPI.delete(id);
      if (response.code === 0) {
        message.success(t('deleted_success'));
        fetchApiKeys();
      } else {
        message.error(response.message || t('deleted_failed'));
      }
    } catch (error) {
      message.error(t('deleted_failed_retry'));
    }
  };

  // 启用/禁用 Key
  const handleToggleKey = async (id) => {
    try {
      const response = await apiKeyAPI.toggle(id);
      if (response.code === 0) {
        message.success(response.data.is_active ? t('enabled') : t('disabled'));
        fetchApiKeys();
      } else {
        message.error(response.message || t('operation_failed'));
      }
    } catch (error) {
      message.error(t('operation_failed_retry'));
    }
  };

  const handleResetKey = () => {
    setConfirmEmail('');
    setResetModalVisible(true);
  };

  const handleResetConfirm = async () => {
    if (confirmEmail !== userInfo?.email) {
      message.error(t('email_mismatch'));
      return;
    }
    setLoading(true);
    try {
      const response = await apiKeyAPI.reset();
      if (response.code === 0) {
        message.success(t('api_key_reset'));
        setResetModalVisible(false);
        fetchUserInfo();
      } else {
        message.error(response.message || t('reset_failed'));
      }
    } catch (error) {
      message.error(t('reset_failed_retry'));
    } finally {
      setLoading(false);
    }
  };

  const copyApiKey = async (key) => {
    const success = await copyToClipboard(key);
    if (success) {
      message.success(t('copy_success'));
    } else {
      message.error(t('copy_failed', 'Copy failed'));
    }
  };

  // 开始编辑名称
  const handleEditName = (record) => {
    setEditingKeyId(record.id);
    setEditingKeyName(record.name);
  };

  // 保存编辑的名称
  const handleSaveName = async (id) => {
    if (!editingKeyName.trim()) {
      message.error(t('please_enter_key_name'));
      return;
    }
    
    try {
      const response = await apiKeyAPI.update(id, { name: editingKeyName.trim() });
      if (response.code === 0) {
        message.success(t('update_success'));
        setEditingKeyId(null);
        fetchApiKeys();
      } else {
        message.error(response.message || t('update_failed'));
      }
    } catch (error) {
      message.error(t('update_failed_retry'));
    }
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditingKeyId(null);
    setEditingKeyName('');
  };

  // 操作栏：只保留启用/禁用和删除，移除复制按钮（创建时已显示一次）
  const keyColumns = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {editingKeyId === record.id ? (
            <>
              <Input
                value={editingKeyName}
                onChange={(e) => setEditingKeyName(e.target.value)}
                size="small"
                style={{ width: 100 }}
                onPressEnter={() => handleSaveName(record.id)}
                autoFocus
              />
              <Button type="text" size="small" icon={<CheckOutlined />} onClick={() => handleSaveName(record.id)} style={{ color: '#52c41a' }} />
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={handleCancelEdit} style={{ color: '#ff4d4f' }} />
            </>
          ) : (
            <>
              <Text strong>{text}</Text>
              {!record.is_active && <Tag color="red">{t('disabled')}</Tag>}
              <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditName(record)} style={{ color: '#1890ff', fontSize: 12 }} />
            </>
          )}
        </div>
      ),
    },
    {
      title: t('api_key'),
      dataIndex: 'api_key',
      key: 'api_key',
      width: 280,
      render: (text) => (
        <Text code style={{ fontSize: 12, fontFamily: 'monospace' }}>
          {text ? text.substring(0, 25) + '...' + text.substring(text.length - 8) : '-'}
        </Text>
      ),
    },
    {
      title: t('limit'),
      dataIndex: 'credit_limit',
      key: 'credit_limit',
      width: 80,
      render: (val) => (
        val > 0 
          ? <Text style={{ color: '#52c41a', fontSize: 12 }}>${val.toFixed(2)}</Text>
          : <Text style={{ color: '#999', fontSize: 12 }}>{t('unlimit')}</Text>
      ),
    },
    {
      title: t('used'),
      dataIndex: 'total_usage',
      key: 'usage',
      width: 120,
      render: (used, record) => {
        const limit = record.credit_limit || 0;
        const usedVal = parseFloat(used || 0); // 后端已转换为美元
        const percent = limit > 0 ? (usedVal / limit * 100).toFixed(1) : null;
        return (
          <div>
            <Text style={{ color: usedVal > 0 ? '#f59e0b' : '#999', fontSize: 12 }}>${usedVal.toFixed(4)}</Text>
            {percent && (
              <>
                <div style={{ fontSize: 10, color: '#999' }}>{percent}%</div>
                <div style={{ width: '100%', height: 3, background: '#f0f0f0', borderRadius: 2, marginTop: 2 }}>
                  <div style={{ 
                    width: `${Math.min(parseFloat(percent), 100)}%`, 
                    height: '100%', 
                    background: parseFloat(percent) > 90 ? '#ff4d4f' : '#1890ff',
                    borderRadius: 2 
                  }} />
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: t('created'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 90,
      render: (text) => <Text style={{ fontSize: 12 }}>{new Date(text).toLocaleDateString('zh-CN')}</Text>,
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button 
            type="text" 
            size="small" 
            icon={record.is_active ? <EyeInvisibleOutlined /> : <EyeOutlined />} 
            onClick={() => handleToggleKey(record.id)}
            style={{ color: record.is_active ? '#f59e0b' : '#52c41a', fontSize: 12 }}
          >
            {record.is_active ? t('disable') : t('enable')}
          </Button>
          {/* 默认Key不可删除 */}
          {!record.is_default && (
            <Popconfirm
              title={t('confirm_delete_key')}
              onConfirm={() => handleDeleteKey(record.id)}
              okText={t('confirm')}
              cancelText={t('cancel')}
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} style={{ fontSize: 12 }}>
                {t('delete')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // 获取API主域名
  const getApiBaseUrl = () => {
    const host = window.location.hostname;
    // 如果是localhost，返回localhost
    if (host === 'localhost' || host === '127.0.0.1') {
      return `${window.location.protocol}//${host}:${window.location.port || 80}`;
    }
    // 如果包含www.，去掉www.并加上api.
    let domain = host;
    if (domain.startsWith('www.')) {
      domain = domain.substring(4);
    }
    return `https://api.${domain}`;
  };

  const apiBaseUrl = getApiBaseUrl();

  // 代码示例
  const codeExamples = {
    curl: `curl -X POST ${apiBaseUrl}/v1/chat/completions \\
  -H "Authorization: Bearer ${userInfo?.api_key || 'YOUR_API_KEY'}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "deepseek-chat",
    "messages": [{"role": "user", "content": "Hello, please introduce yourself"}]
  }'`,

    python: `import openai

# Configure API
openai.api_base = "${apiBaseUrl}/v1"
openai.api_key = "${userInfo?.api_key || 'YOUR_API_KEY'}"

# Call model
response = openai.ChatCompletion.create(
    model="deepseek-chat",
    messages=[{"role": "user", "content": "Hello, please introduce yourself"}]
)

print(response.choices[0].message.content)`,

    nodejs: `const OpenAI = require('openai');

// Configure API
const openai = new OpenAI({
  baseURL: '${apiBaseUrl}/v1',
  apiKey: '${userInfo?.api_key || 'YOUR_API_KEY'}',
});

// Call model
async function main() {
  const completion = await openai.chat.completions.create({
    model: 'deepseek-chat',
    messages: [{ role: 'user', content: 'Hello, please introduce yourself' }],
  });

  console.log(completion.choices[0].message.content);
}

main();`,

    stream: `# Streaming Example (Python)
import openai

openai.api_base = "${apiBaseUrl}/v1"
openai.api_key = "${userInfo?.api_key || 'YOUR_API_KEY'}"

response = openai.ChatCompletion.create(
    model="deepseek-chat",
    messages=[{"role": "user", "content": "Write a poem about spring"}],
    stream=True  # Enable streaming
)

for chunk in response:
    if 'choices' in chunk and len(chunk.choices) > 0:
        delta = chunk.choices[0].delta
        if 'content' in delta:
            print(delta.content, end='', flush=True)`,
  };

  const tabItems = [
    {
      key: 'curl',
      label: t('tab_curl'),
      children: <pre style={{ margin: 0, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{codeExamples.curl}</pre>,
    },
    {
      key: 'python',
      label: t('tab_python'),
      children: <pre style={{ margin: 0, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{codeExamples.python}</pre>,
    },
    {
      key: 'nodejs',
      label: t('tab_nodejs'),
      children: <pre style={{ margin: 0, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{codeExamples.nodejs}</pre>,
    },
    {
      key: 'stream',
      label: t('streaming_output'),
      children: <pre style={{ margin: 0, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{codeExamples.stream}</pre>,
    },
  ];

  // 模型列表表格列定义
  const modelColumns = [
    {
      title: t('model_name'),
      dataIndex: 'model_name',
      key: 'model_name',
      width: 200,
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text code style={{ fontSize: 13 }}>{text}</Text>
          <CopyOutlined
            style={{ cursor: 'pointer', color: '#1890ff', fontSize: 14 }}
            onClick={async (e) => {
              e.stopPropagation();
              const success = await copyToClipboard(text);
              if (success) {
                message.success(t('copied'));
              }
            }}
          />
        </div>
      ),
    },
    {
      title: t('display_name'),
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: t('provider'),
      dataIndex: 'provider',
      key: 'provider',
      render: (provider) => (
        <span className="uf-tag info" style={{ fontSize: 12 }}>
          {typeof provider === 'object' ? provider?.display_name : provider}
        </span>
      ),
    },
  ];

  return (
    <div className="user-page-wrapper">
      <div className="user-page-container">
        {/* 页面标题 */}
        <div className="page-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="title-icon">
              <ApiOutlined />
            </div>
            {t('api_key_management')}
          </div>
        </div>

      {/* Key 管理区域 */}
      <div className="content-section">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="section-title">
            <ApiOutlined />
            <span>{t('my_api_keys')}</span>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
            {t('create_key')}
          </Button>
        </div>

        <div className="section-body">
          <Alert
            message={t('key_tip')}
            type="info"
            showIcon
            style={{ marginBottom: 16, borderRadius: 6 }}
          />

          <Table
            dataSource={apiKeys.slice((keyPage - 1) * keyPageSize, keyPage * keyPageSize)}
            columns={keyColumns}
            rowKey="id"
            pagination={{
              current: keyPage,
              pageSize: keyPageSize,
              total: apiKeys.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['5', '10', '20', '50'],
              showTotal: (total, range) => t('pagination_total', { start: range[0], end: range[1], total }),
              onChange: (page, pageSize) => {
                setKeyPage(page);
                setKeyPageSize(pageSize);
              },
              locale: {
                items_per_page: t('pagination_items_per_page'),
                jump_to: t('pagination_jump_to'),
                page: t('pagination_page'),
              },
            }}
            locale={{ emptyText: t('no_keys_yet') }}
          />
        </div>
      </div>

      {/* 接入指南 */}
      <div className="content-section">
        <div className="section-header">
          <div className="section-title">
            <CodeOutlined />
            <span>{t('integration_guide')}</span>
          </div>
        </div>
        <div className="section-body">

        {/* 快速开始说明 */}
        <Alert
          message={t('openai_compatible')}
          description={
            <div style={{ fontSize: 13, lineHeight: 1.8 }}>
              <p style={{ margin: '0 0 8px 0' }}>{t('openai_compatible_desc')}</p>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>{t('openai_feature_1')}</li>
                <li>{t('openai_feature_2')}</li>
                <li>{t('openai_feature_3')}</li>
              </ul>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 20, borderRadius: 6 }}
        />

        <div className="code-block">
          <div className="code-block-header">
            <span className="code-block-title">{t('request_example')}</span>
            <button
              className="uf-btn-default"
              style={{ height: 28, padding: '0 12px', fontSize: 12 }}
              onClick={async () => {
                const success = await copyToClipboard(codeExamples[activeCodeTab] || codeExamples.curl);
                if (success) {
                  message.success(t('copied'));
                }
              }}
            >
              <CopyOutlined />
              {t('copy_code')}
            </button>
          </div>
          <Tabs
            activeKey={activeCodeTab}
            onChange={setActiveCodeTab}
            items={tabItems}
            style={{ padding: '0 16px' }}
            tabBarStyle={{ marginBottom: 0, borderBottom: '1px solid #3d3d4d' }}
          />
          <style>{`
            .ant-tabs-nav .ant-tabs-tab {
              color: rgba(255, 255, 255, 0.85) !important;
            }
            .ant-tabs-nav .ant-tabs-tab:hover {
              color: #ffffff !important;
            }
            .ant-tabs-nav .ant-tabs-tab-active .ant-tabs-tab-btn {
              color: #1890ff !important;
            }
          `}</style>
        </div>

        <div style={{ marginTop: 20, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="uf-tag info">{t('openai_compatible')}</div>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{t('standard_api')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="uf-tag success">{t('sse_streaming')}</div>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{t('realtime_output')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="uf-tag warning">{t('pay_per_use')}</div>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{t('accurate_billing')}</span>
          </div>
        </div>
        </div>
      </div>

      {/* 可用模型列表 */}
      <div className="content-section" style={{ borderBottom: 'none', marginBottom: 0 }}>
        <div className="section-header">
          <div className="section-title">
            <ApiOutlined />
            <span>{t('available_models')}</span>
          </div>
        </div>
        <div className="section-body">
          <Alert
            message={t('model_hint')}
            type="info"
            showIcon
            style={{ marginBottom: 16, borderRadius: 6 }}
          />

          <Table
            dataSource={models.slice((modelPage - 1) * modelPageSize, modelPage * modelPageSize)}
            columns={modelColumns}
            rowKey="model_name"
            pagination={{
              current: modelPage,
              pageSize: modelPageSize,
              total: models.length,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) => t('pagination_total', { start: range[0], end: range[1], total }),
              locale: {
                items_per_page: t('pagination_items_per_page'),
                jump_to: t('pagination_jump_to'),
                page: t('pagination_page'),
              },
              onChange: (page, pageSize) => {
                setModelPage(page);
                setModelPageSize(pageSize);
              },
              onShowSizeChange: (current, size) => {
                setModelPage(1);
                setModelPageSize(size);
              },
            }}
            size="small"
            style={{ borderRadius: 6, overflow: 'hidden' }}
          />
        </div>
      </div>

      {/* 创建 Key 弹窗 */}
      <Modal
        title={t('create_new_key')}
        open={createModalVisible}
        onOk={handleCreateKey}
        onCancel={() => {
          setCreateModalVisible(false);
          setNewKeyName('');
          setNewCreditLimit(0);
        }}
        confirmLoading={createLoading}
        okText={t('create')}
        cancelText={t('cancel')}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            {t('key_name')} <span style={{ color: '#ff4d4f' }}>*</span>
          </label>
          <Input
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            placeholder={t('key_name_example')}
            size="large"
          />
          <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
            {t('key_name_hint')}
          </div>
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            {t('credit_limit')} <span style={{ color: '#999', fontWeight: 'normal' }}>({t('optional')})</span>
          </label>
          {/* 计算剩余可用额度：余额 - 其他key已设置的额度总和 */}
          {(() => {
            const userBalance = userInfo?.balance || 0;
            const otherKeysCreditSum = apiKeys.reduce((sum, k) => sum + (k.credit_limit || 0), 0);
            const maxAvailable = Math.max(0, userBalance - otherKeysCreditSum);
            return (
              <>
                <InputNumber
                  value={newCreditLimit}
                  onChange={(val) => setNewCreditLimit(val || 0)}
                  min={0}
                  max={maxAvailable > 0 ? maxAvailable : 0}
                  step={1}
                  style={{ width: '100%' }}
                  size="large"
                  placeholder={t('zero_no_limit')}
                  addonAfter="$"
                  disabled={maxAvailable <= 0}
                />
                <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                  {t('credit_limit_hint')}
                </div>
                <div style={{ marginTop: 4, fontSize: 12, color: '#1890ff' }}>
                  {t('current_balance')}: ${userBalance.toFixed(2)} | 
                  {t('available_credit')}: ${maxAvailable.toFixed(2)}
                </div>
                {maxAvailable <= 0 && (
                  <div style={{ marginTop: 4, fontSize: 12, color: '#ff4d4f' }}>
                    {t('no_available_credit')}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </Modal>

      {/* 新 Key 显示弹窗（只显示一次） */}
      <Modal
        title={`✅ ${t('key_created_success')}`}
        open={!!newlyCreatedKey}
        onOk={() => {
          copyApiKey(newlyCreatedKey?.api_key);
          setNewlyCreatedKey(null);
        }}
        onCancel={() => setNewlyCreatedKey(null)}
        okText={t('copy_and_close')}
        cancelText={t('close')}
      >
        <Alert
          message={t('copy_save_key')}
          description={t('key_show_once')}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <div style={{ 
          background: '#f8fafc', 
          border: '1px solid #e5e7eb', 
          borderRadius: 6, 
          padding: '12px 16px',
          fontFamily: 'monospace',
          fontSize: 13,
          wordBreak: 'break-all',
          lineHeight: 1.6,
          marginBottom: 12
        }}>
          {newlyCreatedKey?.api_key}
        </div>
        <div style={{ fontSize: 14, color: '#666' }}>
          <strong>{t('name')}:</strong> {newlyCreatedKey?.name}
        </div>
      </Modal>

      {/* 重置弹窗 */}
      <Modal
        title={t('confirm_reset_api_key')}
        open={resetModalVisible}
        onOk={handleResetConfirm}
        onCancel={() => setResetModalVisible(false)}
        confirmLoading={loading}
        okText={t('confirm_reset')}
        cancelText={t('cancel')}
        okButtonProps={{ danger: true }}
      >
        <Alert
          message={t('reset_irreversible')}
          type="error"
          showIcon
          style={{ marginBottom: 16, borderRadius: 6 }}
        />
        <div style={{ marginBottom: 12, fontSize: 14 }}>
          {t('enter_email_confirm')} <Text strong>{userInfo?.email}</Text> {t('to_confirm')}:
        </div>
        <Input
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          placeholder={userInfo?.email}
          size="large"
          style={{ borderRadius: 6 }}
        />
      </Modal>
      </div>
    </div>
  );
};

export default APIKeys;
